import { ГРАНЬ } from '../celi-gran';
import { DRR_HINT } from './drr-explanation';
import { mergeOrder } from '../clusters/visible';
/**
 * Колонки таблицы кампаний: что показывать и в каком порядке.
 *
 * Раскладка — в `COLUMNS` ниже, там же и разбор расхождений с эталоном.
 * Здесь только устройство: из чего состоит описание колонки.
 */

export type ColKey =
  | 'products'
  | 'spark'
  | 'rules'
  | 'budget'
  | 'limitDay'
  | 'cpmBid'
  | 'spend'
  | 'spendYest'
  | 'views'
  | 'clicks'
  | 'ctr'
  | 'cpc'
  | 'atbs'
  | 'orders'
  | 'crCart'
  | 'crOrder'
  | 'revenue'
  | 'drr'
  | 'cpo'
  | 'canceled';

/**
 * `setting` — не число, а НАСТРОЙКИ: расписание, автопополнение, лимит.
 *
 * Они стоят в той же таблице не для симметрии. Вопрос «почему кампания
 * встала» решается сравнением расхода с лимитом, а «почему счёт пуст» —
 * взглядом на автопополнение рядом с бюджетом. Держать их в другом месте
 * значит заставлять помнить числа при переходе.
 *
 * ТРИ СТОЛБЦА СЛИЛИСЬ В ОДИН 15.08.2026, И ЭТО НЕ ЭКОНОМИЯ РАДИ КРАСОТЫ.
 * Каждый из трёх занимал ширину своего заголовка — «Автопополнение» это
 * четырнадцать знаков, — а сообщал в тридцати строках из тридцати шести
 * одно и то же: «круглосуточно», «выключено», «без ограничений». То есть
 * треть ширины экрана уходила на сообщение, что НИЧЕГО не настроено,
 * причём самым заметным способом из возможных. Теперь это три значка,
 * и место занимает только заданное.
 *
 * Такие столбцы не сортируются: «включено» не больше и не меньше
 * «выключено».
 *
 * `cell` — то же самое, но для столбцов, которые рисуют своё: полоску
 * фотографий и столбики динамики. ОНИ СТАЛИ КОЛОНКАМИ 18.08.2026,
 * а до того были прибиты к разметке гвоздями. В эталоне они стоят
 * в списке «Колонки» наравне с остальными — и правильно: полоска
 * фотографий занимает больше места, чем любые три числа, а нужна
 * далеко не всем и не всегда.
 */
export type ColKind = 'money' | 'int' | 'percent' | 'setting' | 'cell';

/**
 * ═══════════════════════════════════════════════════════════════════════
 * СКОЛЬКО ЗНАКОВ ПОСЛЕ ЗАПЯТОЙ — СВОЙСТВО СТОЛБЦА, А НЕ ВЕЛИЧИНЫ ЧИСЛА.
 *
 * Здесь стояло правило «меньше десяти тысяч — с копейками, больше —
 * без», одно на все денежные столбцы. Эталон устроен иначе, и различает
 * он не размер, а СМЫСЛ:
 *
 *   ▸ РАСХОД И БЮДЖЕТ — всегда с копейками: «549,00», «18 902,40».
 *     Это то, что списано со счёта, и последние два знака в такой
 *     строке — не украшение, а сходимость с выпиской.
 *
 *   ▸ ВЫРУЧКА И ЦЕНА ЗАКАЗА — целыми: «201 089», «137 ₽». Это оценка
 *     Wildberries по своему окну атрибуции, и копейки в ней обещают
 *     точность, которой там нет.
 *
 *   ▸ ДРР — один знак: «18,4 %», «9,4 %». А начиная с сотни — ни одного:
 *     «1 101 %», а не «1 101,0 %». Кампания с ДРР за тысячу процентов
 *     не станет понятнее от десятой доли.
 *
 * У нас все проценты печатались двумя знаками, и «1 101 %» выходило
 * как «1101,00 %» — заодно без разделителя тысяч, потому что считалось
 * это `toFixed`, а не `Intl`.
 * ═══════════════════════════════════════════════════════════════════════
 */
export type ColЗнаков = 0 | 1 | 2;

/**
 * Насколько громко звучит столбец.
 *
 * ТАБЛИЦА ИЗ ВОСЕМНАДЦАТИ ОДИНАКОВО ЯРКИХ ЧИСЕЛ НЕ ЧИТАЕТСЯ. Глаз
 * цепляется за всё сразу, то есть ни за что. В трекере это решено
 * тремя уровнями громкости, и решено верно: показы и корзина —
 * подробности, расход и ДРР — то, ради чего смотрят.
 */
export type ColTone = 'strong' | 'plain' | 'muted';

/**
 * Чем красить столбец.
 *
 *   rel     — сравнением с остальными кампаниями (доли и конверсии);
 *   budget  — по абсолютному порогу: ноль на счёте останавливает показы,
 *             и это плохо независимо от того, сколько у соседей;
 *   orders  — есть заказы или нет; ноль заказов — всегда плохо;
 *   goal    — сравнением С ПРЕДЕЛОМ ЧЕЛОВЕКА, а не с соседями;
 *   none    — не красить вовсе.
 *
 * ЧЕМ `goal` ОТЛИЧАЕТСЯ ОТ `rel`, И ПОЧЕМУ ЭТО НЕ ОТТЕНОК СМЫСЛА.
 * `rel` отвечает на вопрос «как эта кампания на фоне остальных»,
 * `goal` — на вопрос «устраивает ли меня это». Второй вопрос человек
 * и задаёт, открывая таблицу; на первый он согласен только потому,
 * что второго ему не предлагали.
 *
 * 28.08 владелец задал предел ДРР и написал: «цвета в кампаниях
 * не обновились. Всё как было и осталось». Так и было: столбец ДРР
 * красился медианой показанных строк. В кабинете, где все кампании
 * плохи одинаково, медиана делает их все нормальными; в кабинете,
 * где все хороши, — половину плохими. Предел человека не участвовал
 * никак, хотя окно целей обещало ровно его.
 *
 * АБСОЛЮТНЫЕ ДЕНЬГИ СРАВНЕНИЕМ НЕ КРАСЯТСЯ. «Потратил меньше медианы» —
 * это не хорошо и не плохо, это просто другая кампания. Красить их
 * значит заливать таблицу цветом, который ничего не значит.
 */
export type ColPaint = 'rel' | 'goal' | 'budget' | 'orders' | 'none';

export interface Col {
  key: ColKey;
  title: string;
  /** Заголовок группы над столбцом. Пустая строка — группы нет. */
  group: string;
  /** Показывать ли, пока человек не выбрал иначе. */
  on: boolean;
  kind: ColKind;
  /**
   * Знаков после запятой. Не задано — по роду: деньги и проценты
   * с двумя, целые без. Довод — у `ColЗнаков`.
   */
  знаков?: ColЗнаков;
  /**
   * Что печатается ПОСЛЕ числа: «197 ₽».
   *
   * Ровно один столбец в эталоне носит знак рубля прямо в клетке —
   * «Заказ стоит». У расхода он вынесен в заголовок группы
   * («РАСХОД, ₽»), у бюджета и выручки его нет вовсе. Разнобой
   * не наш, но перерисовывать эталон по своему вкусу — не наше дело.
   */
  хвост?: string;
  /**
   * У этого показателя «меньше — лучше».
   *
   * Нужно для раскраски: ДРР и стоимость заказа читаются наоборот
   * от конверсий, и покрасить их одинаково значит показать зелёным
   * ровно то, из-за чего кампанию стоит остановить.
   */
  lowerBetter?: boolean;
  tone: ColTone;
  paint: ColPaint;
  /** Что это за число — по наведению. */
  hint: string;
  /**
   * ПОЧЕМУ КЛЕТКА МОЖЕТ ОКАЗАТЬСЯ ПУСТОЙ — в подсказке у прочерка.
   *
   * Ответ 32 от 23.08: «прочерк и подсказка-причина в `title`. Причина
   * обязательна: „нет данных“ без причины читается как ошибка сервиса».
   *
   * ПОЛЕ ОБЯЗАТЕЛЬНОЕ, ХОТЯ ПУСТЫМИ БЫВАЮТ НЕ ВСЕ СТОЛБЦЫ. Необязательное
   * означало бы, что новый столбец заводится без причины и молча
   * показывает голый прочерк — ровно то, от чего ответ 32 и спасает.
   * Сторож `columns.spec.ts` держит это поле непустым у каждого.
   */
  пусто: string;
}

/**
 * ПОРЯДОК И СОСТАВ ГРУПП — ПО ЭТАЛОНУ (`design/Bidra-Kabinet.dc.html`,
 * массив `colGroups`). Кампания → Ставки → Расход → Трафик → Воронка →
 * Результат: сколько поставили, сколько ушло, что за это пришло,
 * во что превратилось, чем кончилось. Читая слева направо, человек
 * проходит тот же путь, что и рубль.
 *
 * ЧЕГО ЗДЕСЬ НЕТ И ПОЧЕМУ — три расхождения с эталоном, все намеренные:
 *
 *   • «НАЗВАНИЕ И НОМЕР» — в эталоне это выключаемая колонка. У нас
 *     название кампании и есть ссылка на неё; спрятав его, человек
 *     получит таблицу, из которой некуда нажать. Галочка, ломающая
 *     экран, — это не соответствие образцу, а перенесённая опечатка.
 *
 *   • «УДЕРЖАНИЯ» — у нас нет таких данных. Расход мы берём
 *     из `fullstats`, а удержания живут в `/adv/v1/upd`, который
 *     не синхронизируется (замер 31.07.2026: за одни сутки fullstats
 *     дал 9428 ₽, upd — 2416 ₽; это РАЗНЫЕ числа, а не одно и то же
 *     с погрешностью). Пустой столбец хуже отсутствующего.
 *
 *   • «ПОЗИЦИЯ» — средняя позиция есть только по запросу (`avg_pos`
 *     в `cluster_stats_daily`), и она показана на экране запросов,
 *     где она настоящая. Свернуть тысячу запросов в одну «позицию
 *     кампании» значит выдать число, которое человек не сможет
 *     сверить ни с чем — и заплатить за него полным проходом
 *     по статистике кластеров при каждой загрузке списка.
 *
 * ЧАСТЬ КОЛОНОК ВЫКЛЮЧЕНА ИЗНАЧАЛЬНО, и это не экономия места ради
 * красоты. Таблица, которая не влезает в экран, заводит горизонтальную
 * прокрутку — а с ней первый столбец уезжает, и человек смотрит на числа,
 * не видя, чьи они. Умолчания взяты из эталона.
 *
 * ВЫБОР ЧЕЛОВЕКА ПЕРЕЖИВАЕТ ПЕРЕЗАГРУЗКУ. Настройка таблицы, которую
 * приходится делать заново каждое утро, — это настройка, которой
 * не пользуются.
 */
export const COLUMNS: readonly Col[] = [
  {
    key: 'products',
    title: 'Товары',
    group: 'Кампания',
    on: true,
    kind: 'cell',
    tone: 'plain',
    paint: 'none',
    hint: 'Фотографии товаров кампании и сколько их всего. Наведите на фотографию, чтобы увеличить',
    пусто: 'Wildberries не отдал по этой кампании ни одного артикула',
  },
  {
    /**
     * ДИНАМИКА СТОИТ ДО ЧИСЕЛ, А НЕ ПОСЛЕ. Она отвечает на вопрос
     * «куда движется», и ответ на него нужен раньше, чем уровень:
     * падающая кампания с приличным ДРР опаснее ровной с плохим.
     */
    key: 'spark',
    title: 'Динамика',
    group: 'Кампания',
    on: true,
    kind: 'cell',
    tone: 'plain',
    paint: 'none',
    hint: 'Заказы по дням за выбранный период и изменение ДРР к равному промежутку перед ним',
    пусто: 'Сравнивать не с чем: за выбранный промежуток заказов не было',
  },
  {
    key: 'rules',
    title: 'Правила',
    group: 'Кампания',
    on: true,
    kind: 'setting',
    tone: 'plain',
    paint: 'none',
    hint:
      'Что сервис делает с кампанией сам: часы показов, автопополнение счёта ' +
      'и лимит расхода. Нажмите значок, чтобы настроить',
    пусто: 'Ни расписание, ни автопополнение, ни лимит по этой кампании не заданы',
  },
  {
    key: 'budget',
    title: 'Бюджет',
    group: 'Ставки',
    on: true,
    kind: 'money',
    tone: 'plain',
    paint: 'budget',
    hint: 'Остаток на счёте кампании. Ноль означает, что показы встали, даже если Wildberries считает кампанию активной',
    пусто:
      'Остаток счёта ещё ни разу не выгружался — обычно он приходит в первую минуту после подключения',
  },
  {
    /**
     * СУТОЧНЫЙ ЛИМИТ РЯДОМ С БЮДЖЕТОМ И СТАВКОЙ — так в эталоне,
     * и так же осмысленно: все три отвечают на вопрос «сколько мы
     * готовы отдать», а расход рядом отвечает, сколько отдали.
     *
     * ПУСТО ОЗНАЧАЕТ «НЕ ЗАДАН», А НЕ НОЛЬ. Ноль — это «не тратить
     * вовсе», и такой лимит остановил бы кампанию немедленно.
     */
    key: 'limitDay',
    title: 'Лимит',
    group: 'Ставки',
    on: false,
    kind: 'money',
    tone: 'muted',
    paint: 'none',
    hint: 'Суточный потолок расхода, заданный сервисом. Пусто — не задан',
    пусто:
      'Суточный лимит не задан. Пусто — не то же самое, что ноль: ноль остановил бы показы',
  },
  {
    /**
     * СТАВКА CPM — ВИЛКОЙ, КОГДА ОНА НЕ ОДНА.
     *
     * Wildberries держит ставку не у кампании, а у пары «товар +
     * площадка». Обычно они равны, и в ячейке одно число. Когда нет —
     * показывается «от и до», а не среднее и не наибольшее: человек,
     * который видит «255» там, где половина товаров стоит на 90,
     * поднимет ставку не тому.
     *
     * Сортируется по верхнему краю вилки: вопрос при сортировке
     * всегда «где ставим дороже всего».
     */
    key: 'cpmBid',
    title: 'Ставка CPM',
    group: 'Ставки',
    on: false,
    kind: 'money',
    tone: 'plain',
    paint: 'none',
    hint: 'Ставка кампании за тысячу показов. Диапазон — если у товаров или площадок ставки разные',
    пусто: 'Ставок у кампании нет вовсе — поэтому она числится активной и не тратит',
  },
  {
    key: 'spend',
    title: 'За период',
    group: 'Расход',
    on: true,
    kind: 'money',
    tone: 'strong',
    paint: 'none',
    hint: 'Сколько кампания потратила за выбранный период',
    пусто: 'Статистика за этот промежуток ещё не выгружена',
  },
  {
    /**
     * «ВЧЕРА» НЕ ЗАВИСИТ ОТ ВЫБРАННОГО ПЕРИОДА, и в этом весь смысл
     * столбца: сумма за месяц ничего не говорит о сегодняшнем темпе.
     * Считается от конца периода — ровно как плитка над таблицей.
     * Два разных «вчера» на одном экране развалили бы доверие
     * ко всем числам сразу.
     */
    key: 'spendYest',
    title: 'Вчера',
    group: 'Расход',
    on: false,
    kind: 'money',
    tone: 'muted',
    paint: 'none',
    hint: 'Расход за вчерашние сутки. От выбранного периода не зависит — по нему виден сегодняшний темп',
    пусто: 'За вчера статистика ещё не пришла: Wildberries отдаёт её с задержкой',
  },
  {
    key: 'clicks',
    title: 'Клики',
    group: 'Трафик',
    on: true,
    kind: 'int',
    tone: 'plain',
    paint: 'none',
    hint: 'Сколько раз по нему перешли',
    пусто: 'Статистика за этот промежуток ещё не выгружена',
  },
  {
    key: 'ctr',
    title: 'CTR',
    group: 'Трафик',
    on: true,
    kind: 'percent',
    tone: 'plain',
    paint: 'rel',
    hint: 'Клики ÷ показы: насколько объявление вообще заметно',
    пусто: 'Не из чего посчитать: показов за промежуток не было',
  },
  {
    key: 'views',
    title: 'Показы',
    group: 'Трафик',
    on: false,
    kind: 'int',
    tone: 'muted',
    paint: 'none',
    hint: 'Сколько раз объявление показали',
    пусто: 'Статистика за этот промежуток ещё не выгружена',
  },
  {
    /**
     * ЦЕНА КЛИКА В «ТРАФИКЕ», А НЕ РЯДОМ СО СТОИМОСТЬЮ ЗАКАЗА, — так
     * и в эталоне, и по смыслу.
     *
     * Она про вход: во сколько обходится приведённый человек. Стоимость
     * заказа — про выход. Заказ по 500 ₽ бывает от двух разных болезней:
     * при дешёвом клике люди приходят и не покупают (карточка, цена),
     * при дорогом — трафик покупается втридорога ещё на входе (ставка,
     * запросы). Одна стоимость заказа этих случаев не различает,
     * а лечат их по-разному.
     */
    key: 'cpc',
    title: 'Клик, ₽',
    group: 'Трафик',
    on: false,
    kind: 'money',
    lowerBetter: true,
    tone: 'plain',
    paint: 'rel',
    hint: 'Во сколько обошёлся один клик. Меньше — лучше',
    пусто: 'Не из чего посчитать: переходов за промежуток не было',
  },
  {
    key: 'atbs',
    title: 'В корзину',
    group: 'Воронка',
    on: true,
    kind: 'int',
    tone: 'muted',
    paint: 'none',
    hint: 'Сколько раз товар положили в корзину',
    пусто: 'Статистика за этот промежуток ещё не выгружена',
  },
  {
    key: 'orders',
    title: 'Заказы',
    group: 'Воронка',
    on: true,
    kind: 'int',
    tone: 'strong',
    paint: 'orders',
    hint: 'Сколько заказов Wildberries отнёс к этой кампании',
    пусто: 'Статистика за этот промежуток ещё не выгружена',
  },
  {
    key: 'crCart',
    title: '% в корзину',
    group: 'Воронка',
    on: false,
    kind: 'percent',
    tone: 'plain',
    paint: 'rel',
    hint: 'В корзину ÷ клики: насколько карточка убеждает после клика',
    пусто: 'Не из чего посчитать: переходов за промежуток не было',
  },
  {
    key: 'crOrder',
    title: '% в заказ',
    group: 'Воронка',
    on: false,
    kind: 'percent',
    tone: 'plain',
    paint: 'rel',
    hint: 'Заказы ÷ в корзину: доходит ли корзина до заказа',
    пусто: 'Не из чего посчитать: корзин за промежуток не было',
  },
  {
    key: 'revenue',
    знаков: 0,
    title: 'Выручка',
    group: 'Результат',
    on: true,
    kind: 'money',
    tone: 'plain',
    paint: 'none',
    hint: 'Выручка по отчёту Wildberries. Он относит её к кампании сам, по своему окну атрибуции, поэтому она обычно выше той, что сойдётся с отчётом о продажах',
    пусто: 'Wildberries не отдал сумму заказов за этот промежуток',
  },
  {
    key: 'drr',
    знаков: 1,
    title: 'ДРР',
    group: 'Результат',
    on: true,
    kind: 'percent',
    lowerBetter: true,
    tone: 'strong',
    paint: 'goal',
    hint: DRR_HINT,
    пусто:
      'Не из чего посчитать: заказов за промежуток не было, а делить расход не на что',
  },
  {
    key: 'cpo',
    хвост: ' ₽',
    знаков: 0,
    title: 'Заказ стоит',
    group: 'Результат',
    on: true,
    kind: 'money',
    lowerBetter: true,
    tone: 'plain',
    paint: 'goal',
    hint: 'Во сколько обошёлся один заказ. Меньше — лучше. Цвет — по вашему пределу из целей кабинета',
    пусто: 'Не из чего посчитать: заказов за промежуток не было',
  },
  {
    /**
     * ОТМЕНЫ НЕ ВЫЧИТАЮТСЯ ИЗ ЗАКАЗОВ, и это не небрежность.
     *
     * Wildberries считает заказ в день оформления, а отмену — в день,
     * когда она случилась. Дни разные. Вычти одно из другого — и в
     * недельном отчёте появится минус там, где отменили позапрошлый
     * заказ. Столбец стоит рядом и говорит своё.
     *
     * НЕ КРАСИТСЯ СРАВНЕНИЕМ, и потому у столбца НЕТ пометки
     * «меньше — лучше». У большой кампании отмен больше просто потому,
     * что она большая; красить это красным значит ругать её за размер.
     * Пометка нужна раскраске и только ей — поставить её при `paint:
     * 'none'` значит завести признак, который ничего не делает
     * и однажды кого-нибудь обманет.
     */
    key: 'canceled',
    title: 'Отмены',
    group: 'Результат',
    on: false,
    kind: 'int',
    tone: 'muted',
    paint: 'none',
    hint: 'Сколько заказов отменили. Считается по дню отмены, поэтому из заказов за тот же период не вычитается',
    пусто: 'Статистика отмен за этот промежуток ещё не выгружена',
  },
];

const STORE = 'rk_cols';

/**
 * Какие колонки показывать сейчас.
 *
 * НЕИЗВЕСТНЫЙ КЛЮЧ В СОХРАНЁННОМ НАБОРЕ ПРОСТО ИГНОРИРУЕТСЯ. Появится
 * новая колонка — она возьмёт своё умолчание, а не исчезнет из-за того,
 * что в старой записи её нет. Обратное означало бы, что человек,
 * однажды настроивший таблицу, больше никогда не увидит ничего нового.
 */
export function visibleCols(saved: Record<string, unknown> | null): Record<ColKey, boolean> {
  const out = {} as Record<ColKey, boolean>;
  for (const c of COLUMNS) {
    const v = saved ? saved[c.key] : undefined;
    out[c.key] = typeof v === 'boolean' ? v : c.on;
  }
  return out;
}

export function readCols(): Record<ColKey, boolean> {
  try {
    const raw = localStorage.getItem(STORE);
    return visibleCols(raw ? (JSON.parse(raw) as Record<string, unknown>) : null);
  } catch {
    // Испорченная запись не должна ломать экран: покажем умолчания
    return visibleCols(null);
  }
}

export function saveCols(on: Record<ColKey, boolean>): void {
  try {
    localStorage.setItem(STORE, JSON.stringify(on));
  } catch {
    // Хранилище может быть недоступно — таблица от этого не перестаёт
    // работать, просто выбор не переживёт перезагрузку
  }
}

/* ═══════════════════════════════════════════════════════════════════════
   ПОРЯДОК СТОЛБЦОВ — СВОЙ У КАЖДОГО ЧЕЛОВЕКА.

   08.09.2026 владелец: «также в общей таблице кампаний сделать колонки
   как в кластерах, с возможностью перетягивания».

   ═══════════════════════════════════════════════════════════════════════
   СЧЁТ БЕРЁТСЯ У КЛАСТЕРОВ, А НЕ ПИШЕТСЯ ЗАНОВО.

   `mergeOrder` и `moveBefore` там уже написаны и знают два неочевидных
   случая: новая графа встаёт К СВОЕЙ СОСЕДКЕ СЛЕВА, а не в конец
   (иначе одно обновление выглядит по-разному у двух людей за соседними
   столами), и перенос — это «встать на место», а не «поменяться
   местами» (обмен зашвыривает соседку через полсписка).

   Написать здесь свои значило бы завести вторую правду о том же
   и разойтись с ней на первом же выпуске.

   СВОИМ ОСТАЁТСЯ ТОЛЬКО КЛЮЧ: набор граф у кампаний другой, и общая
   запись перемешала бы их с кластерными.
   ═══════════════════════════════════════════════════════════════════ */
const STORE_ORDER = 'wb.campaigns.cols.order.v1';

/** Все ключи в эталонном порядке — том, в котором объявлены столбцы. */
export const ВСЕ_КЛЮЧИ: ColKey[] = COLUMNS.map((c) => c.key);

export function читатьПорядок(storage: Pick<Storage, 'getItem'> = localStorage): ColKey[] {
  try {
    const raw = storage.getItem(STORE_ORDER);
    if (raw === null || raw === '') return [...ВСЕ_КЛЮЧИ];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [...ВСЕ_КЛЮЧИ];
    const свои = parsed.filter((v): v is string => typeof v === 'string');
    return mergeOrder(свои, ВСЕ_КЛЮЧИ) as ColKey[];
  } catch {
    /* Хранилище недоступно — таблица от этого не перестаёт работать. */
    return [...ВСЕ_КЛЮЧИ];
  }
}

export function сохранитьПорядок(
  storage: Pick<Storage, 'setItem'>,
  order: readonly ColKey[],
): void {
  try {
    storage.setItem(STORE_ORDER, JSON.stringify([...order]));
  } catch {
    /* не сохранилось — не беда */
  }
}

/**
 * «Вернуть как было» — СТИРАЕМ ЗАПИСЬ, А НЕ ПИШЕМ В НЕЁ ЭТАЛОН.
 *
 * Записанный эталон — это «человек выбрал ровно это», и следующий
 * выпуск, где столбцов стало больше, обязан будет его уважать.
 * Отсутствие записи означает «не выбирал» — и новый порядок приедет сам.
 */
export function забытьПорядок(storage: Pick<Storage, 'removeItem'> = localStorage): void {
  try {
    storage.removeItem(STORE_ORDER);
  } catch {
    /* нечего стирать */
  }
}

/** Столбцы в выбранном порядке — и только видимые. */
export function вПорядке(порядок: readonly ColKey[], on: Record<ColKey, boolean>): Col[] {
  const поКлючу = new Map(COLUMNS.map((c) => [c.key, c]));
  return порядок
    .map((k) => поКлючу.get(k))
    .filter((c): c is Col => c !== undefined && on[c.key]);
}

// ── Сортировка ───────────────────────────────────────────────────────

export type SortDir = 'asc' | 'desc';

/**
 * Сравнение двух значений одного столбца.
 *
 * «НЕТ ДАННЫХ» ВСЕГДА ВНИЗУ, В ЛЮБУЮ СТОРОНУ, и это не мелочь.
 * Прочерк — не самое маленькое число, а отсутствие числа. Сортируя
 * по возрастанию ДРР, человек ищет лучшие кампании; всплыви наверх
 * два десятка прочерков — он увидит «лучшими» те, о которых мы просто
 * ничего не знаем, и это худший из возможных ответов на его вопрос.
 */
export function compareValues(a: number | null, b: number | null, dir: SortDir): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return dir === 'asc' ? a - b : b - a;
}

export function sortBy<T>(
  rows: readonly T[],
  value: (row: T) => number | null,
  dir: SortDir,
): T[] {
  // Копия, а не сортировка на месте: массив приходит из запроса,
  // и менять его значит менять то, что кэш считает неизменным
  return [...rows].sort((x, y) => compareValues(value(x), value(y), dir));
}

// ── Раскраска относительно кабинета ──────────────────────────────────

/**
 * Медиана — опора для раскраски.
 *
 * СРЕДНЕЕ ЗДЕСЬ НЕ ГОДИТСЯ. Одна кампания с ДРР в 300 % утащит среднее
 * так, что все остальные окажутся «хорошими», — а именно её и надо
 * заметить. Медиана к выбросам равнодушна.
 *
 * СТРОКИ БЕЗ ЧИСЛА В РАСЧЁТ НЕ ИДУТ. Кампания без выручки не делает
 * кабинет хуже — о ней просто нечего сказать.
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * У ГРУППЫ ДВА ИМЕНИ, И ЭТО НЕ НЕБРЕЖНОСТЬ ЭТАЛОНА.
 *
 * В окне «Колонки» группа зовётся «Расход» (`colGroups`,
 * `design/Bidra-Kabinet.dc.html` строка 3603), а в шапке таблицы над
 * теми же столбцами стоит «Расход, ₽» (строка 682). Разница по делу:
 * в списке галочек человек выбирает ПОКАЗАТЕЛЬ, и единица там лишняя;
 * в шапке под ней идут числа, и единица избавляет от «₽» в каждой
 * клетке. У «Ставок» её нет — там и рубли, и проценты, и позиции.
 *
 * ПЕРВАЯ ПРАВКА СДЕЛАЛА ЭТО ОДНИМ ИМЕНЕМ — переименовала группу целиком.
 * `columns.spec.ts` покраснел сразу и был прав: он сверяет состав групп
 * с `colGroups` эталона, то есть со списком галочек, а не с шапкой.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function заголовокГруппы(имя: string): string {
  return имя === 'Расход' ? 'Расход, ₽' : имя;
}

export function median(values: ReadonlyArray<number | null>): number | null {
  const xs = values.filter((v): v is number => v !== null).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid]! : (xs[mid - 1]! + xs[mid]!) / 2;
}

/**
 * Насколько число хорошо на фоне кабинета: 'good' | 'bad' | null.
 *
 * ПОРОГИ ВЗЯТЫ ИЗ ТРЕКЕРА, где они прожили год: заметно лучше — это
 * четверть разницы, заметно хуже — сорок процентов. Уже — и красится
 * половина таблицы, шире — и не красится ничего.
 *
 * У ДРР И ЦЕНЫ ЗАКАЗА ВСЁ НАОБОРОТ, и перепутать это значит показать
 * зелёным ровно то, из-за чего кампанию стоит остановить.
 */
export function relClass(
  value: number | null,
  base: number | null,
  lowerBetter = false,
): 'good' | 'bad' | null {
  if (value === null || base === null || !(base > 0)) return null;
  const r = value / base;
  if (lowerBetter) return r <= 0.75 ? 'good' : r >= 1.4 ? 'bad' : null;
  return r >= 1.25 ? 'good' : r <= 0.7 ? 'bad' : null;
}

/**
 * Цвет ячейки: 'good' | 'bad' | 'warn' | null.
 *
 * СОБРАНО В ОДНО МЕСТО НАМЕРЕННО. Правил четыре, и разбросанные
 * по разметке они разойдутся: остаток счёта красится порогом, заказы —
 * наличием, доли — сравнением, деньги не красятся вовсе. Пока это
 * четыре ветки одной функции, спорить им негде.
 */
export function paintOf(
  col: Col,
  value: number | null,
  base: number | null,
): 'good' | 'bad' | 'warn' | 'serious' | null {
  switch (col.paint) {
    case 'budget':
      // Ноль на счёте останавливает показы, даже если Wildberries
      // считает кампанию активной. Пятьсот рублей — запас меньше суток
      // у обычной кампании: это ещё не беда, но уже повод посмотреть.
      if (value === null) return null;
      return value <= 0 ? 'bad' : value < 500 ? 'warn' : null;
    case 'orders':
      // Ноль заказов плох безотносительно соседей: реклама, которая
      // не продаёт, не бывает «нормальной на общем фоне»
      if (value === null) return null;
      return value > 0 ? 'good' : 'bad';
    case 'rel':
      return relClass(value, base, col.lowerBetter);
    case 'goal':
      return goalClass(value, base);
    case 'none':
      return null;
  }
}

/**
 * Цвет по ПРЕДЕЛУ ЧЕЛОВЕКА: 'good' | 'warn' | 'bad' | null.
 *
 * СТУПЕНИ ТЕ ЖЕ, ЧТО ВЕЗДЕ: до предела — хорошо, до полутора пределов —
 * «на грани», выше — слив. Тот же множитель (`ГРАНЬ`) считает вердикты
 * служба, им же подписано окно целей («сливает — выше 27 %») и плитки
 * наверху. Четвёртое место со своими ступенями спорило бы с тремя
 * сразу, и спорило бы цветом — там, где человек его не проверяет.
 *
 * ГРАНИЦЫ ВКЛЮЧИТЕЛЬНЫ СНИЗУ: поставивший 18 ожидает, что 18,0 %
 * его устроит.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * СРЕДНЯЯ СТУПЕНЬ ЗОВЁТСЯ `serious`, А НЕ `warn`, И ЭТО НЕ ПРИДИРКА.
 *
 * В наборе два разных оранжево-жёлтых: `--warn` (#fab219) и `--serious`
 * (#ec835a). Эталон различает их по смыслу: `--warn` — «кончаются
 * деньги на счёте», `--serious` — «на грани» у лестницы целей
 * (`goalFg` в эталоне: `edge: var(--serious-ink)`).
 *
 * Мы звали ступень `warn`, а `тонСтупени` собирает цвет из имени —
 * и ДРР «на грани» выходил жёлтым вместо оранжевого. Кластеры при этом
 * красили тот же вердикт правильно (`vid-po-etalonu.spec.ts`: «rgb(180,
 * 83,31) = --serious-ink вердикт „на грани"»), то есть один и тот же
 * ответ на двух экранах имел два цвета.
 * ═══════════════════════════════════════════════════════════════════════
 *
 * ПРЕДЕЛА НЕТ — НЕ КРАСИМ ВОВСЕ. Так бывает у цены заказа, когда она
 * считается от ДРР, а среднего чека в кабинете ещё нет. Вернуться
 * к сравнению с соседями было бы хуже молчания: цвет означал бы
 * то одно, то другое, и человек не знал бы, что перед ним.
 */
export function goalClass(
  value: number | null,
  предел: number | null,
): 'good' | 'serious' | 'bad' | null {
  if (value === null || предел === null || !(предел > 0)) return null;
  if (value <= предел) return 'good';
  if (value <= предел * ГРАНЬ) return 'serious';
  return 'bad';
}
